import { getToken, setToken } from './platform'
import { tg } from './telegram'
import type {
  Booking,
  BookingClub,
  BookingSlots,
  DeskBooking,
  DeskOut,
  AdminCoach,
  CoachFormat,
  CoachInput,
  CoachListItem,
  CoachRequestItem,
  MyCoach,
  MyCoachRequest,
  AdminDeal,
  BlockedPlayer,
  Deal,
  DealInput,
  ExternalGame,
  AdminPlayer,
  Chat,
  ChatMessage,
  Club,
  Game,
  GameInvite,
  InviteCandidate,
  Match,
  MatchPreview,
  MyInvite,
  OnboardingAnswers,
  PlayerMe,
  PlayerRecord,
  ReportLinks,
  Tournament,
  TSettings,
  RatingHistoryItem,
  SetScore,
} from './types'

const BASE = import.meta.env.VITE_API_BASE ?? '/api'

let sessionExpired: (() => void) | null = null
/** App подписывается, чтобы показать экран входа, когда сервер отверг токен */
export function onSessionExpired(fn: () => void): void {
  sessionExpired = fn
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')

  const token = getToken()
  if (tg?.initData) {
    headers.set('Authorization', `tma ${tg.initData}`)
  } else if (token) {
    // Нативное приложение: токен сессии, полученный при входе
    headers.set('Authorization', `Bearer ${token}`)
  } else if (import.meta.env.DEV) {
    // Локальная разработка вне Telegram. На сервере работает только
    // при DEV_ALLOW_UNSIGNED=1 — не включайте это в продакшене.
    headers.set('X-Dev-User', JSON.stringify({ id: 1, first_name: 'Dev' }))
  }

  const response = await fetch(`${BASE}${path}`, { ...init, headers })

  if (response.status === 401 && token && !tg?.initData) {
    // Сессия отозвана или истекла — возвращаем человека на экран входа
    setToken(null)
    sessionExpired?.()
  }

  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? detail
    } catch {
      /* тело не json */
    }
    throw new ApiError(detail, response.status)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  authTelegramStart: () =>
    request<{ nonce: string; url: string; deep: string; expires_in: number }>('/auth/telegram/start', { method: 'POST' }),
  authTelegramPoll: (nonce: string, platform: string) =>
    request<{ status: 'pending' | 'ok' | 'linked' | 'taken' | 'expired'; token: string | null }>(
      '/auth/telegram/poll',
      { method: 'POST', body: JSON.stringify({ nonce, platform }) },
    ),
  authApple: (identity_token: string, first_name: string | null, language: string, platform: string) =>
    request<{ status: string; token: string | null }>('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ identity_token, first_name, language, platform }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  deleteAccount: () => request<void>('/players/me', { method: 'DELETE' }),

  me: () => request<PlayerMe>('/players/me'),
  onboarding: (body: OnboardingAnswers) =>
    request<PlayerMe>('/players/me/onboarding', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMe: (body: Partial<PlayerMe>) =>
    request<PlayerMe>('/players/me', { method: 'PATCH', body: JSON.stringify(body) }),

  clubs: (city?: string) =>
    request<Club[]>(`/clubs${city ? `?city=${encodeURIComponent(city)}` : ''}`),

  games: (params: { days?: number; only_my_level?: boolean; city?: string } = {}) => {
    const qs = new URLSearchParams()
    if (params.days) qs.set('days', String(params.days))
    if (params.only_my_level) qs.set('only_my_level', 'true')
    if (params.city) qs.set('city', params.city)
    const suffix = qs.toString()
    return request<Game[]>(`/games${suffix ? `?${suffix}` : ''}`)
  },
  externalGames: () => request<ExternalGame[]>('/external-games'),
  setSpot: (gameId: number, spot: number | null, playerId?: number) =>
    request<Game>(`/games/${gameId}/spot`, {
      method: 'PUT',
      body: JSON.stringify({ spot, player_id: playerId ?? null }),
    }),
  removePlayer: (gameId: number, playerId: number) =>
    request<Game>(`/games/${gameId}/players/${playerId}`, { method: 'DELETE' }),
  deals: () => request<Deal[]>('/deals'),
  clickDeal: (id: number) => request<void>(`/deals/${id}/click`, { method: 'POST' }),
  adminDeals: () => request<AdminDeal[]>('/admin/deals'),
  adminSaveDeal: (body: DealInput, id?: number) =>
    request<Deal>(id ? `/admin/deals/${id}` : '/admin/deals', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(body),
    }),
  adminDeleteDeal: (id: number) => request<void>(`/admin/deals/${id}`, { method: 'DELETE' }),
  adminPreviewDeal: (url: string) =>
    request<{ title: string | null; image_url: string | null; price: number | null; old_price: number | null; shop: string | null }>(
      '/admin/deals/preview',
      { method: 'POST', body: JSON.stringify({ url }) },
    ),
  coaches: () => request<CoachListItem[]>('/coaches'),
  myCoach: () => request<MyCoach | null>('/coaches/me'),
  saveMyCoach: (body: CoachInput) => request<MyCoach>('/coaches/me', { method: 'PUT', body: JSON.stringify(body) }),
  deleteMyCoach: () => request<void>('/coaches/me', { method: 'DELETE' }),
  coachInbox: () => request<CoachRequestItem[]>('/coaches/me/requests'),
  sendCoachRequest: (coachId: number, body: { format: CoachFormat; when_text: string | null; message: string | null }) =>
    request<MyCoachRequest>(`/coaches/${coachId}/requests`, { method: 'POST', body: JSON.stringify(body) }),
  answerCoachRequest: (id: number, answer: 'accept' | 'decline') =>
    request<CoachRequestItem>(`/coach-requests/${id}/${answer}`, { method: 'POST' }),
  adminCoaches: () => request<AdminCoach[]>('/admin/coaches'),
  adminReviewCoach: (id: number, action: 'approve' | 'reject' | 'pause') =>
    request<void>(`/admin/coaches/${id}`, { method: 'POST', body: JSON.stringify({ action }) }),
  createGame: (body: Record<string, unknown>) =>
    request<Game>('/games', { method: 'POST', body: JSON.stringify(body) }),
  game: (id: number) => request<Game>(`/games/${id}`),
  updateGame: (
    id: number,
    body: {
      court_booked?: boolean
      notes?: string | null
      price_per_player?: number | null
      starts_at?: string
      club_id?: number
      duration_min?: number
    },
  ) => request<Game>(`/games/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  prepareShareCard: (id: number) =>
    request<{ id: string }>(`/games/${id}/share-card`, { method: 'POST' }),
  join: (id: number) => request<Game>(`/games/${id}/join`, { method: 'POST' }),
  leave: (id: number) => request<Game>(`/games/${id}/leave`, { method: 'POST' }),
  cancelGame: (id: number) => request<void>(`/games/${id}`, { method: 'DELETE' }),

  organizerRequest: () => request<PlayerMe>('/players/me/organizer-request', { method: 'POST' }),
  adminOrganizer: (playerId: number, action: 'approve' | 'reject' | 'revoke') =>
    request<AdminPlayer>(`/admin/players/${playerId}/organizer`, { method: 'POST', body: JSON.stringify({ action }) }),
  adminPlayers: (q = '') =>
    request<AdminPlayer[]>(`/admin/players${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  adminSetLevel: (playerId: number, level: number, reset_calibration: boolean) =>
    request<AdminPlayer>(`/admin/players/${playerId}`, {
      method: 'PATCH',
      body: JSON.stringify({ level, reset_calibration }),
    }),

  chat: (gameId: number, before?: number) =>
    request<Chat>(`/games/${gameId}/chat${before ? `?before=${before}` : ''}`),
  sendChat: (gameId: number, text: string) =>
    request<ChatMessage>(`/games/${gameId}/chat`, { method: 'POST', body: JSON.stringify({ text }) }),
  readChat: (gameId: number, lastId: number) =>
    request<void>(`/games/${gameId}/chat/read`, { method: 'POST', body: JSON.stringify({ last_id: lastId }) }),
  pinChat: (gameId: number, messageId: number | null) =>
    request<void>(`/games/${gameId}/chat/pin`, { method: 'PUT', body: JSON.stringify({ message_id: messageId }) }),
  deleteChat: (messageId: number) => request<void>(`/chat/${messageId}`, { method: 'DELETE' }),
  reportChat: (messageId: number, reason?: string) =>
    request<{ hidden_for_all: boolean }>(`/chat/${messageId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  blocks: () => request<BlockedPlayer[]>('/blocks'),
  blockPlayer: (playerId: number) => request<void>(`/blocks/${playerId}`, { method: 'PUT' }),
  unblockPlayer: (playerId: number) => request<void>(`/blocks/${playerId}`, { method: 'DELETE' }),

  inviteCandidates: (gameId: number, q = '') =>
    request<InviteCandidate[]>(
      `/games/${gameId}/invite-candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  invite: (gameId: number, playerId: number) =>
    request<GameInvite>(`/games/${gameId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ player_id: playerId }),
    }),
  gameInvites: (gameId: number) => request<GameInvite[]>(`/games/${gameId}/invites`),
  claimInvite: (gameId: number, inviterId: number) =>
    request<void>(`/games/${gameId}/invites/claim`, {
      method: 'POST',
      body: JSON.stringify({ inviter_id: inviterId }),
    }),
  myInvites: () => request<MyInvite[]>('/invites/mine'),
  declineInvite: (id: number) => request<void>(`/invites/${id}/decline`, { method: 'POST' }),

  tournament: (gameId: number) => request<Tournament>(`/games/${gameId}/tournament`),
  /** Итоги турнира: бот присылает картинку и PDF в личку */
  reportSend: (gameId: number) =>
    request<{ ok: boolean }>(`/games/${gameId}/tournament/report/send`, { method: 'POST' }),
  /** Подписанные ссылки на картинку и PDF (живут сутки) */
  reportLinks: (gameId: number) => request<ReportLinks>(`/games/${gameId}/tournament/report/links`),
  tournamentAction: (gameId: number, action: 'start' | 'reset' | 'finish' | 'next-round' | 'undo-round') =>
    request<Tournament>(`/games/${gameId}/tournament/${action}`, { method: 'POST' }),
  setTournamentScore: (matchId: number, score_a: number | null, score_b: number | null, golden: 'a' | 'b' | null = null) =>
    request<Tournament>(`/tournament/matches/${matchId}`, {
      method: 'PUT',
      body: JSON.stringify({ score_a, score_b, golden }),
    }),
  /** Таймер раунда («Король корта») */
  tournamentTimer: (gameId: number, action: 'start' | 'stop') =>
    request<Tournament>(`/games/${gameId}/tournament/timer`, { method: 'POST', body: JSON.stringify({ action }) }),
  tournamentSettings: (gameId: number, body: TSettings) =>
    request<Tournament>(`/games/${gameId}/tournament/settings`, { method: 'PUT', body: JSON.stringify(body) }),
  setPairs: (gameId: number, pairs: [number, number][]) =>
    request<Tournament>(`/games/${gameId}/tournament/pairs`, { method: 'PUT', body: JSON.stringify({ pairs }) }),
  setPlayerGender: (gameId: number, playerId: number, gender: 'm' | 'f') =>
    request<void>(`/games/${gameId}/players/${playerId}/gender`, {
      method: 'PUT',
      body: JSON.stringify({ gender }),
    }),
  setPaid: (gameId: number, slotId: number, paid: boolean) =>
    request<void>(`/games/${gameId}/slots/${slotId}`, { method: 'PATCH', body: JSON.stringify({ paid }) }),
  matches: (gameId: number) => request<Match[]>(`/games/${gameId}/matches`),
  previewMatch: (gameId: number, body: { team_a: number[]; team_b: number[]; sets: SetScore[] }) =>
    request<MatchPreview>(`/games/${gameId}/matches/preview`, { method: 'POST', body: JSON.stringify(body) }),
  submitMatch: (gameId: number, body: { team_a: number[]; team_b: number[]; sets: SetScore[] }) =>
    request<Match>(`/games/${gameId}/matches`, { method: 'POST', body: JSON.stringify(body) }),
  confirmMatch: (id: number) => request<Match>(`/matches/${id}/confirm`, { method: 'POST' }),
  disputeMatch: (id: number) => request<Match>(`/matches/${id}/dispute`, { method: 'POST' }),
  withdrawMatch: (id: number) => request<Match>(`/matches/${id}/withdraw`, { method: 'POST' }),
  record: () => request<PlayerRecord>('/players/me/record'),

  // Заявки на корт (v0.35)
  bookingClubs: () => request<BookingClub[]>('/booking/clubs'),
  bookingSlots: (clubId: number, minutes: number | null, day: string | null) => {
    const q = new URLSearchParams()
    if (minutes) q.set('minutes', String(minutes))
    if (day) q.set('day', day)
    return request<BookingSlots>(`/booking/clubs/${clubId}/slots?${q}`)
  },
  createBooking: (body: { club_id: number; starts_at: string; minutes: number; court: number | null; note: string | null; game_id: number | null }) =>
    request<Booking>('/bookings', { method: 'POST', body: JSON.stringify(body) }),
  myBookings: () => request<Booking[]>('/bookings/mine'),
  cancelBooking: (id: number) => request<Booking>(`/bookings/${id}/cancel`, { method: 'POST' }),
  clubDesk: (clubId: number) => request<DeskOut>(`/club-desk/${clubId}`),
  deskAnswer: (id: number, accept: boolean) =>
    request<DeskBooking>(`/club-desk/bookings/${id}/answer`, { method: 'POST', body: JSON.stringify({ accept }) }),
  deskCancel: (id: number) => request<DeskBooking>(`/club-desk/bookings/${id}/cancel`, { method: 'POST' }),
  deskNoShow: (id: number, value: boolean) =>
    request<DeskBooking>(`/club-desk/bookings/${id}/no-show`, { method: 'POST', body: JSON.stringify({ value }) }),
  ratingHistory: () => request<RatingHistoryItem[]>('/players/me/rating-history'),
}
